import { CalendarEventDto } from '@/types/calendar';

export interface DayTaskCompletion {
  completed: number;
  total: number;
  /** Ціле число 0-100. */
  percent: number;
}

export interface DayTaskCompletionParams {
  /** День колонки календаря, "YYYY-MM-DD". */
  day: string;
  /** Чиї призначення рахуємо (автор події). */
  creatorId: number;
  /** Поточний місцевий час клієнта, "YYYY-MM-DDTHH:MM:SS" - у тому ж вигляді, що й `scheduledLdt`. */
  clientNowIso: string;
}

/** `scheduledLdt` до вигляду "YYYY-MM-DDTHH:MM" - рядки такого вигляду порівнюються як час. */
const toMinuteIso = (ldt: string): string => ldt.replace(' ', 'T').slice(0, 16);

/** Стан призначення на картці події. */
export type EventCompletionStatus = 'completed' | 'missed';

export interface EventCompletionParams {
  /** Чиї призначення рахуємо (автор події). */
  creatorId: number;
  /** Поточний місцевий час клієнта, "YYYY-MM-DDTHH:MM:SS" - у тому ж вигляді, що й `scheduledLdt`. */
  clientNowIso: string;
}

/**
 * Виконана - завжди (і завчасно); невиконана - лише коли її час у клієнта вже настав.
 * `null` - подія іншого автора або її час ще не настав.
 */
export function getEventCompletionStatus(
  event: CalendarEventDto,
  { creatorId, clientNowIso }: EventCompletionParams,
): EventCompletionStatus | null {
  if (event.createdBy?.id !== creatorId || !event.scheduledLdt) return null;
  if (event.isCompleted) return 'completed';
  return toMinuteIso(event.scheduledLdt) > toMinuteIso(clientNowIso) ? null : 'missed';
}

/**
 * Виконання призначень одного автора за день календаря клієнта: події будь-якого типу, які він створив.
 *
 * Минулий день - усі події дня. Сьогодні (за часом клієнта) - лише ті, час яких у клієнта вже настав, плюс
 * виконані завчасно. Майбутній день або день без таких подій - null.
 */
export function getDayTaskCompletion(
  events: CalendarEventDto[],
  { day, creatorId, clientNowIso }: DayTaskCompletionParams,
): DayTaskCompletion | null {
  if (day > clientNowIso.slice(0, 10)) return null;

  let total = 0;
  let completed = 0;
  for (const event of events) {
    if (!event.scheduledLdt || toMinuteIso(event.scheduledLdt).slice(0, 10) !== day) continue;
    const status = getEventCompletionStatus(event, { creatorId, clientNowIso });
    if (!status) continue;
    total += 1;
    if (status === 'completed') completed += 1;
  }

  if (total === 0) return null;
  return { completed, total, percent: Math.round((completed / total) * 100) };
}
